import { useQuery, useQueryClient } from '@tanstack/react-query';
import uuid from 'react-native-uuid';
import { isEqual } from 'lodash';
import { ApiLogger, logUserEvent } from 'fmc-digital-common';
import { createContext } from 'react';
const useESignDetailsQuery = ({
  url,
  authToken,
  loanNumber,
  select,
  refetchInterval,
  setRefetchInterval,
  enabled,
  xApiKey,
  clearToastMessageProps,
  setEsignToastMessage,
  setRefreshCount,
  refreshCount
}) => {
  const correlationId = uuid.v4().toString();
  const client = useQueryClient();
  const config = {
    method: 'get',
    url: `${url}/${loanNumber}/esign/packages`,
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'x-correlation-id': correlationId,
      'x-api-key': xApiKey
    }
  };
  //@ts-ignore
  return useQuery({
    //@ts-ignore
    queryKey: ['e-sign', loanNumber],
    queryFn: () => ApiLogger(config),
    select,
    refetchInterval,
    enabled,
    //@ts-ignore
    structuralSharing: (oldData, newData) => {
      if (oldData && newData) {
        const oldDataArray = [];
        oldDataArray.push(oldData);
        const newDataArray = [];
        newDataArray.push(newData);
        const oldPackages = reduceLoanPackages(oldDataArray);
        const newPackages = reduceLoanPackages(newDataArray);
        const oSigned = oldPackages.map(oe => ({
          signed: oe.signed
        }));
        const nSigned = newPackages.map(ne => ({
          signed: ne.signed
        }));
        if (!isEqual(oSigned, nSigned)) {
          setRefetchInterval(false);
          clearToastMessageProps();
          setEsignToastMessage({
            isShowToastMessage: true,
            message: 'Your disclosures were signed successfully',
            buttonText: '',
            type: 'LIPSignDisclosurePopUp',
            trackingItemName: '',
            toastType: 'SUCCESS',
            bodyText: 'You can see them under the filters “Completed” and “All Tasks”'
          });
        }
      }
      return newData;
    },
    onSuccess: data => {
      if (data && refreshCount < 6) {
        setRefreshCount(refreshCount + 1);
        setTimeout(() => {
          client.invalidateQueries({
            queryKey: ['e-sign', loanNumber]
          }); // Trigger a retry
        }, 30000);
      }
    }
  });
};
const reduceLoanPackages = packages => packages.reduce((result, current) => {
  if (current && Array.isArray(current.loanPackages) && current.loanPackages.length) {
    return result.concat(current.loanPackages.sort((a, b) => b.signingFlag - a.signingFlag));
  }
  return result;
}, []);
const selectDisclosures = response => {
  let disclosures;
  if (response) {
    disclosures = response;
  }
  return disclosures;
};
const getToken = async (item, handleTokenResponse, loanNumber, authToken, url, xApiKey) => {
  logUserEvent({
    action: 'FetchingESignTokenStarted',
    type: 'ESign',
    description: `fetching token started for url ${item?.docURL}`
  });
  try {
    if (loanNumber) {
      const correlationId = uuid.v4().toString();
      const response = await ApiLogger({
        url: `${url}/${loanNumber}/esign/token`,
        method: 'post',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'x-correlation-id': correlationId,
          'x-api-key': xApiKey
        },
        data: {
          encodedUrl: item?.docURL,
          loanNumber
        }
      });
      if (response) {
        logUserEvent({
          action: 'FetchingESignTokenSuccess',
          type: 'ESign',
          description: `fetching token started for url ${item?.docURL}`
        });
        handleTokenResponse({
          token: response.accessToken,
          url: response.eSignUrl,
          activeDisclosedUserName: item?.borrowerName
        });
      }
    } else {
      logUserEvent({
        action: 'FetchingESignTokenFailed',
        type: 'ESign',
        description: `using the fallback document url ${item?.docURL}`
      });
      if (item?.docURL) handleTokenResponse({
        token: '',
        url: item?.docURL,
        activeDisclosedUserName: item?.borrowerName
      });
    }
  } catch (e) {
    logUserEvent({
      action: 'FetchingESignTokenFailed',
      type: 'ESign',
      description: `using the fallback document url ${item?.docURL}`
    });
    if (item?.docURL) handleTokenResponse({
      token: '',
      url: item?.docURL,
      activeDisclosedUserName: item?.borrowerName
    });
  } finally {
    logUserEvent({
      action: 'FetchingESignTokenCompleted',
      type: 'ESign'
    });
  }
};
const ESignContext = /*#__PURE__*/createContext({
  loanNumber: '',
  url: '',
  authToken: '',
  refetchInterval: false,
  xApiKey: '',
  esignStatusDetails: {
    infoData: false,
    errorData: false,
    esignStatusMessage: '',
    isEnabledEsign: true
  }
});
export { useESignDetailsQuery, selectDisclosures, getToken, ESignContext };
//# sourceMappingURL=Esign.js.map